import axiosInstance from "../../axiosInstance";
import {
  LeaveRequest,
  LeaveRequestListItem,
  LeaveCancellationRequest,
  SubmitLeaveRequestPayload,
  CalculateLeaveRequestPayload,
  CalculateLeaveRequestResponse,
  OverrideDecision,
  SpringPage,
  LeaveListParams,
  ReviewQueueParams,
  LeaveHistoryParams,
} from "types/leaveRequest";

// Dry run — creates no request, reserves no balance. Same date/session and
// working-day rules as submission, so the returned chargeableDates/Days are
// exactly what submission would charge.
export const calculateLeaveRequest = async (data: CalculateLeaveRequestPayload): Promise<CalculateLeaveRequestResponse> => {
  const response = await axiosInstance.post('/people/leave-requests/calculate', data);
  return response.data;
};

export const submitLeaveRequest = async (data: SubmitLeaveRequestPayload): Promise<LeaveRequest> => {
  const response = await axiosInstance.post('/people/leave-requests', data);
  return response.data;
};

export const getLeaveRequest = async (uuid: string): Promise<LeaveRequest> => {
  const response = await axiosInstance.get(`/people/leave-requests/${uuid}`);
  return response.data;
};

// axios's default array serialization produces status[]=A&status[]=B, which
// Spring's @RequestParam List<String> does not bind — it needs the plain
// repeated form (status=A&status=B) the spec documents.
const repeatedParamsSerializer = (params: Record<string, any>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((v) => search.append(key, String(v)));
    } else {
      search.append(key, String(value));
    }
  });
  return search.toString();
};

// Serves both the employee's Pending Requests tab (status filter passed) and
// Leave History tab (no status filter — the complete record, pending rows
// included). Scoped to the logged-in user; there is no employee identifier.
export const getMyLeaveRequests = async (params: LeaveListParams = {}): Promise<SpringPage<LeaveRequestListItem>> => {
  const response = await axiosInstance.get('/people/me/leave-requests', {
    params: {
      status: params.status,
      leaveAllocationUuid: params.leaveAllocationUuid,
      page: params.page ?? 0,
      size: params.size ?? 20,
      sort: params.sort,
    },
    paramsSerializer: repeatedParamsSerializer,
  });
  return response.data;
};

// Pending requests the logged-in viewer currently holds a real action on —
// this is the HR/approver review screen's data source. Authorization is
// applied inside the query; an empty result is an empty page, not a 403.
// `search` matches employee name server-side, so totalElements/pages come
// back already narrowed to the matches.
export const getLeaveReviewQueue = async (params: ReviewQueueParams = {}): Promise<SpringPage<LeaveRequestListItem>> => {
  const response = await axiosInstance.get('/people/leave-requests/review-queue', {
    params: {
      search: params.search,
      page: params.page ?? 0,
      size: params.size ?? 20,
      sort: params.sort,
    },
  });
  return response.data;
};

// Separate from the review queue — this is decided history (Approved/
// Rejected/Cancelled by default, or narrowed via status). Backs the Admin
// History tab; requires Org Owner/Org Admin/HRM Admin/HRM Manager.
export const getLeaveRequestHistory = async (
  params: LeaveHistoryParams = {}
): Promise<SpringPage<LeaveRequestListItem>> => {
  const response = await axiosInstance.get('/people/leave-requests/history', {
    params: {
      search: params.search,
      employeeUuid: params.employeeUuid,
      leaveAllocationUuid: params.leaveAllocationUuid,
      status: params.status,
      fromDate: params.fromDate,
      toDate: params.toDate,
      page: params.page ?? 0,
      size: params.size ?? 20,
      sort: params.sort,
    },
    paramsSerializer: repeatedParamsSerializer,
  });
  return response.data;
};

export const approveLeaveRequest = async (
  uuid: string,
  version: number,
  reason?: string | null
): Promise<LeaveRequest> => {
  const response = await axiosInstance.post(`/people/leave-requests/${uuid}/approve`, {
    version,
    reason: reason ?? null,
  });
  return response.data;
};

export const rejectLeaveRequest = async (
  uuid: string,
  version: number,
  reason?: string | null
): Promise<LeaveRequest> => {
  const response = await axiosInstance.post(`/people/leave-requests/${uuid}/reject`, {
    version,
    reason: reason ?? null,
  });
  return response.data;
};

export const overrideLeaveRequest = async (
  uuid: string,
  data: { version: number; decision: OverrideDecision; reason: string }
): Promise<LeaveRequest> => {
  const response = await axiosInstance.post(`/people/leave-requests/${uuid}/override`, data);
  return response.data;
};

// Requester-only, for a PENDING or APPROVAL_CONFIGURATION_REQUIRED request.
export const cancelLeaveRequest = async (
  uuid: string,
  version: number,
  reason?: string | null
): Promise<LeaveRequest> => {
  const response = await axiosInstance.post(`/people/leave-requests/${uuid}/cancel`, {
    version,
    reason: reason ?? null,
  });
  return response.data;
};

// For an already-APPROVED leave, before its start date — routes to HR Admin
// review rather than cancelling directly.
export const submitCancellationRequest = async (
  leaveRequestUuid: string,
  data: { version: number; reason: string }
): Promise<LeaveCancellationRequest> => {
  const response = await axiosInstance.post(
    `/people/leave-requests/${leaveRequestUuid}/cancellation-requests`,
    data
  );
  return response.data;
};

export const getCancellationRequest = async (uuid: string): Promise<LeaveCancellationRequest> => {
  const response = await axiosInstance.get(`/people/leave-cancellation-requests/${uuid}`);
  return response.data;
};

// PENDING cancellations the logged-in HR Admin can decide now — the
// cancellation-review screen's data source.
export const getCancellationReviewQueue = async (
  params: ReviewQueueParams = {}
): Promise<SpringPage<LeaveCancellationRequest>> => {
  const response = await axiosInstance.get('/people/leave-cancellation-requests/review-queue', {
    params: { page: params.page ?? 0, size: params.size ?? 20, sort: params.sort },
  });
  return response.data;
};

export const approveCancellationRequest = async (
  uuid: string,
  version: number,
  reason?: string | null
): Promise<LeaveCancellationRequest> => {
  const response = await axiosInstance.post(`/people/leave-cancellation-requests/${uuid}/approve`, {
    version,
    reason: reason ?? null,
  });
  return response.data;
};

export const rejectCancellationRequest = async (
  uuid: string,
  version: number,
  reason?: string | null
): Promise<LeaveCancellationRequest> => {
  const response = await axiosInstance.post(`/people/leave-cancellation-requests/${uuid}/reject`, {
    version,
    reason: reason ?? null,
  });
  return response.data;
};
